import React, { useState } from "react";
import Link from "next/link";
import { Trans, useTranslation } from "next-i18next";
import SelectBox from "./select-box";
import { COUNTRIES } from "../../../lib/countries";

const SURVEY_NS = "observability-survey-2026" as const;

const HUBSPOT_PORTAL_ID = "25310090";
const HUBSPOT_FORM_ID = "7d8468a7-7ffb-4001-8d5e-9377ebec74a4";

/**
 * The report handed over once the form is submitted. Lives in the WordPress
 * media library, like every other gated PDF on the site.
 *
 * Deliberately a RELATIVE path, not the wpadmin.middleware.io URL the file is
 * uploaded under. `download` is only honoured for same-origin URLs — point this
 * at wpadmin and the browser ignores it and opens the PDF in a tab instead.
 * next.config.js rewrites `/wp-content/:path*` to the WordPress origin, so a
 * relative path is served from our own origin and downloads properly. It also
 * resolves correctly on localhost and staging, which a hardcoded
 * https://middleware.io/... URL would not.
 */
const REPORT_PDF_URL = "/wp-content/uploads/2026/04/2026-Observability-Forecast.pdf";
const REPORT_FILENAME = "2026 Observability Forecast.pdf";

const COUNTRY_PLACEHOLDER_ID = "placeholder";

const ObservabilitySurveyForm = () => {
    const { t } = useTranslation(SURVEY_NS);

    const countryPlaceholder = t("form.countryPlaceholder");
    const countryItems = [
        { value: countryPlaceholder, id: COUNTRY_PLACEHOLDER_ID },
        ...COUNTRIES,
    ];

    const [fullname, setFullname] = useState("");
    const [fullnameErr, setFullnameErr] = useState(false);
    const [email, setEmail] = useState("");
    const [emailErr, setEmailErr] = useState(false);
    const [company, setCompany] = useState("");
    const [companyErr, setCompanyErr] = useState(false);
    const [country, setCountry] = useState("");
    const [countryErr, setCountryErr] = useState(false);
    const [consent, setConsent] = useState(false);
    const [consentErr, setConsentErr] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [submitted, setSubmitted] = useState(false);

    /**
     * Starts the download without navigating away. Browsers can decline a
     * programmatic download that lands too long after the click that caused it,
     * so the success panel also renders a plain link as a fallback.
     */
    const downloadReport = () => {
        if (!REPORT_PDF_URL) return;
        const link = document.createElement("a");
        link.href = REPORT_PDF_URL;
        link.download = REPORT_FILENAME;
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const validateInputs = () => {
        const emailRegEx =
            /^[\w-.]+@(?!gmail\.com$|yahoo\.com$|hotmail\.com$|yahoo\.co\.in$|aol\.com$|abc\.com$|xyz\.com$|pqr\.com$|rediffmail\.com$|live\.com$|outlook\.com$|me\.com$|msn\.com$|ymail\.com$)[\w-]+\.[a-zA-Z]{2,}$/;

        const isInvalidName = fullname.trim().length < 3;
        const isInvalidEmail =
            email.length < 2 || !emailRegEx.test(String(email).toLowerCase());
        const isInvalidCompany = company.trim().length < 1;
        const isInvalidCountry = country.length < 1;
        const isInvalidConsent = !consent;

        setFullnameErr(isInvalidName);
        setEmailErr(isInvalidEmail);
        setCompanyErr(isInvalidCompany);
        setCountryErr(isInvalidCountry);
        setConsentErr(isInvalidConsent);

        return (
            !isInvalidName &&
            !isInvalidEmail &&
            !isInvalidCompany &&
            !isInvalidCountry &&
            !isInvalidConsent
        );
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitError("");
        if (!validateInputs()) return;

        const xhr = new XMLHttpRequest();
        const url = `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_ID}`;
        const data = {
            submittedAt: Date.now(),
            fields: [
                { name: "firstname", value: fullname },
                { name: "email", value: email },
                { name: "company", value: company },
                { name: "country", value: country },
                { name: "source_type", value: "Observability Survey 2026" },
            ],
        };

        xhr.open("POST", url);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.onreadystatechange = function () {
            if (xhr.readyState !== 4) return;
            if (xhr.status === 200) {
                setSubmitted(true);
                // Hand the report over immediately; the lead is already captured.
                downloadReport();
            } else {
                setSubmitError(xhr.responseText || t("form.errors.submit"));
            }
        };
        xhr.send(JSON.stringify(data));
    };

    if (submitted) {
        return (
            <div className={"survey-form"}>
                <div className={"form survey-form-success"}>
                    <h4>{t("form.successTitle")}</h4>
                    <p>{t("form.successDescription")}</p>
                    {REPORT_PDF_URL && (
                        <a
                            href={REPORT_PDF_URL}
                            download={REPORT_FILENAME}
                            className={"primary ahref-btn"}
                        >
                            {t("form.downloadCta")}
                        </a>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={"survey-form"}>
            <div className={"form"}>
                <h4>{t("form.title")}</h4>
                <form onSubmit={handleSubmit} noValidate>
                    <div className={"input-wrapper"}>
                        <label>
                            {t("form.fullNameLabel")} <span className={"required"}>*</span>
                        </label>
                        <input
                            type={"text"}
                            value={fullname}
                            placeholder={t("form.fullNamePlaceholder")}
                            className={`contact-input ${fullnameErr ? "is-error" : ""}`}
                            onChange={(e) => {
                                setFullname(e.target.value);
                                if (e.target.value.length > 0) setFullnameErr(false);
                            }}
                        />
                        <h6 className={`form-input-validation ${fullnameErr ? "error" : ""}`}>
                            {t("form.errors.fullName")}
                        </h6>
                    </div>

                    <div className={"input-wrapper"}>
                        <label>
                            {t("form.emailLabel")} <span className={"required"}>*</span>
                        </label>
                        <input
                            type={"email"}
                            value={email}
                            placeholder={t("form.emailPlaceholder")}
                            className={`contact-input ${emailErr ? "is-error" : ""}`}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (e.target.value.length > 0) setEmailErr(false);
                            }}
                        />
                        <h6 className={`form-input-validation ${emailErr ? "error" : ""}`}>
                            {email.length < 1
                                ? t("form.errors.emailRequired")
                                : t("form.errors.emailBusiness")}
                        </h6>
                    </div>

                    <div className={"input-wrapper"}>
                        <label>
                            {t("form.companyLabel")} <span className={"required"}>*</span>
                        </label>
                        <input
                            type={"text"}
                            value={company}
                            placeholder={t("form.companyPlaceholder")}
                            className={`contact-input ${companyErr ? "is-error" : ""}`}
                            onChange={(e) => {
                                setCompany(e.target.value);
                                if (e.target.value.length > 0) setCompanyErr(false);
                            }}
                        />
                        <h6 className={`form-input-validation ${companyErr ? "error" : ""}`}>
                            {t("form.errors.company")}
                        </h6>
                    </div>

                    <div className={"input-wrapper"}>
                        <label>
                            {t("form.countryLabel")} <span className={"required"}>*</span>
                        </label>
                        <SelectBox
                            items={countryItems}
                            selectedValue={country || countryPlaceholder}
                            onChange={(item) => {
                                // The placeholder row is not a real answer.
                                const picked =
                                    item.id === COUNTRY_PLACEHOLDER_ID ? "" : item.value;
                                setCountry(picked);
                                if (picked) setCountryErr(false);
                            }}
                        />
                        <h6 className={`form-input-validation ${countryErr ? "error" : ""}`}>
                            {t("form.errors.country")}
                        </h6>
                    </div>

                    <div className={"input-wrapper consent-wrapper"}>
                        <label className={"consent"}>
                            <input
                                type={"checkbox"}
                                checked={consent}
                                onChange={(e) => {
                                    setConsent(e.target.checked);
                                    if (e.target.checked) setConsentErr(false);
                                }}
                            />
                            <span>
                                <Trans
                                    i18nKey={"form.consent"}
                                    ns={SURVEY_NS}
                                    components={{
                                        privacy: (
                                            <Link href={"/privacy-policy/"} target={"_blank"} />
                                        ),
                                    }}
                                />
                            </span>
                        </label>
                        <h6 className={`form-input-validation ${consentErr ? "error" : ""}`}>
                            {t("form.errors.consent")}
                        </h6>
                    </div>

                    <button type={"submit"} className={"primary"}>
                        {t("form.submit")}
                    </button>
                    {submitError && (
                        <h6 className={"form-input-validation error"}>{submitError}</h6>
                    )}
                </form>
            </div>
        </div>
    );
};

export default ObservabilitySurveyForm;
